import { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import {
  Monitor,
  Eye,
  Users,
  Database,
  Settings,
  PlayCircle,
  StopCircle,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { useDemoMode, DemoScenario } from './DemoContext';

interface DemoEnvToggleProps {
  onToggle: (isDemoMode: boolean) => void;
}

export default function DemoEnvToggle({ onToggle }: DemoEnvToggleProps) {
  const { isDemoMode, activeScenario, scenarios, setIsDemoMode, setActiveScenario } = useDemoMode();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [demoTimer, setDemoTimer] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isDemoMode) {
      interval = setInterval(() => {
        setDemoTimer(prev => prev + 1);
      }, 1000);
    } else {
      setDemoTimer(0);
    }
    return () => clearInterval(interval);
  }, [isDemoMode]);

  const handleToggle = async () => {
    setIsTransitioning(true);

    // Simulate mode transition
    setTimeout(() => {
      const newMode = !isDemoMode;
      setIsDemoMode(newMode);
      onToggle(newMode);
      setIsTransitioning(false);
    }, 1500);
  };

  const loadScenario = (scenario: DemoScenario) => {
    setActiveScenario(scenario);
    // In a real implementation, this would trigger backend changes
    console.log(`Loading demo scenario: ${scenario.name}`);
  };

  const resetDemo = () => {
    setDemoTimer(0);
    const firstScenario = scenarios[0];
    if (firstScenario) {
      setActiveScenario(firstScenario);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="bg-gray-900 border-[#3A352F]">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Monitor className="w-6 h-6 text-cyan-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">Demo Environment Control</h3>
              <p className="text-[#A8A29E] text-sm">Switch between live and demo modes for presentations</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {isDemoMode && (
              <Badge variant="outline" className="text-yellow-400 border-yellow-400">
                Demo Active: {formatTime(demoTimer)}
              </Badge>
            )}
            <Badge
              variant="outline"
              className={isDemoMode ? "text-orange-400 border-orange-400" : "text-green-400 border-green-400"}
            >
              {isDemoMode ? '🎭 Demo Mode' : '🔴 Live Mode'}
            </Badge>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center space-x-4 mb-6 p-4 bg-[#292524] rounded-lg">
          <Button
            onClick={handleToggle}
            disabled={isTransitioning}
            size="lg"
            variant={isDemoMode ? "default" : "outline"}
            className={isDemoMode ? "bg-orange-600 hover:bg-orange-700" : "border-green-600 text-green-400 hover:bg-green-900"}
          >
            {isTransitioning ? (
              <RotateCcw className="w-5 h-5 mr-2 animate-spin" />
            ) : isDemoMode ? (
              <StopCircle className="w-5 h-5 mr-2" />
            ) : (
              <PlayCircle className="w-5 h-5 mr-2" />
            )}
            {isTransitioning ? 'Transitioning...' : isDemoMode ? 'Exit Demo Mode' : 'Enter Demo Mode'}
          </Button>

          {isDemoMode && (
            <Button
              onClick={resetDemo}
              variant="outline"
              className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          )}
        </div>

        {/* Demo Scenarios (only visible in demo mode) */}
        {isDemoMode && (
          <div className="space-y-4">
            <h4 className="font-semibold text-white flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              Demo Scenarios
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {scenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    activeScenario.id === scenario.id
                      ? 'border-cyan-500 bg-cyan-900/20'
                      : 'border-[#4A443D] bg-[#292524] hover:border-[#5A534B]'
                  }`}
                  onClick={() => loadScenario(scenario)}
                >
                  <h5 className="font-semibold text-white mb-2">{scenario.name}</h5>
                  <p className="text-[#A8A29E] text-xs mb-3">{scenario.description}</p>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex justify-between">
                      <span>Agents:</span>
                      <span className="text-cyan-400">{scenario.metrics.agents}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Modules:</span>
                      <span className="text-green-400">{scenario.metrics.modules}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Alerts:</span>
                      <span className={scenario.metrics.alerts > 0 ? "text-ag-critical" : "text-[#A8A29E]"}>
                        {scenario.metrics.alerts}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Efficiency:</span>
                      <span className="text-yellow-400">{scenario.metrics.efficiency}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Active Scenario Details */}
            <div className="p-4 bg-[#292524] rounded-lg">
              <h5 className="font-semibold text-white mb-3 flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                Active Scenario: {activeScenario.name}
              </h5>

              <div className="space-y-2">
                <h6 className="text-sm font-medium text-[#A8A29E]">Recent Events:</h6>
                {activeScenario.events.map((event, index) => (
                  <div key={index} className="flex items-start space-x-2 text-sm">
                    <div className="w-2 h-2 rounded-full bg-[#D4825F] mt-2 flex-shrink-0"></div>
                    <span className="text-[#A8A29E]">{event}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Status Information */}
        <div className="mt-6 p-4 bg-[#292524] rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <Users className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <div className="text-lg font-bold text-white">{isDemoMode ? activeScenario.metrics.agents : '???'}</div>
              <div className="text-xs text-[#A8A29E]">Active Agents</div>
            </div>
            <div>
              <Database className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <div className="text-lg font-bold text-white">{isDemoMode ? activeScenario.metrics.modules : '???'}</div>
              <div className="text-xs text-[#A8A29E]">Deployed Modules</div>
            </div>
            <div>
              <AlertTriangle className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <div className="text-lg font-bold text-white">{isDemoMode ? activeScenario.metrics.alerts : '???'}</div>
              <div className="text-xs text-[#A8A29E]">System Alerts</div>
            </div>
            <div>
              <Monitor className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <div className="text-lg font-bold text-white">{isDemoMode ? `${activeScenario.metrics.efficiency}%` : '???'}</div>
              <div className="text-xs text-[#A8A29E]">Efficiency</div>
            </div>
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
          <h6 className="text-sm font-semibold text-blue-300 mb-2">About This View</h6>
          <ul className="text-xs text-blue-200 space-y-1">
            <li>• What you're seeing: Agentomy governing a simulated fleet of AI agents across a typical operational day.</li>
            <li>• Each scenario demonstrates governance at a different deployment scale -- from startup teams to enterprise fleets.</li>
            <li>• The governance pipeline, audit trail, and behavioral monitoring shown here are the same systems that run in production.</li>
            <li>• Switch scenarios to explore how governance adapts to different environments.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
